/* eslint-disable string-to-lingui/missing-lingui-transformation */

import type {NavigateFunction} from 'react-router-dom'
import {useNavigate} from 'react-router-dom'
import styled, {css} from 'styled-components'
import {v4 as uuid} from 'uuid'

import {Badge, Box, Button, Inline, Stack, Text, tokens} from '@pleo-io/telescope'
import {ArrowRight, CheckCircleFilled, Lock} from '@pleo-io/telescope-icons'

import {exhaustiveCheck} from '@product-web/utils'

import {
    Container,
    containerQuery,
    useContainerQuery,
} from '~/generic-ui/container-queries/container'

import magnifyingGlassIllustration from '../images/magnifying-glass.svg'
import walletAddIllustration from '../images/wallet-add.svg'

const MOBILE_BREAKPOINT = 400
const CONTAINER_QUERY_NAME = 'MilestoneCard'

//#region Components

type Milestone = 'COMPANY_VERIFICATION' | 'WALLET'
type Status = 'COMPLETE' | 'INCOMPLETE' | 'IN_PROGRESS'

type MilestoneCardProps = {
    milestone: Milestone
    status: Status
    disabled?: boolean
    active?: boolean
    headingLevel: 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
}

export const MilestoneCard = ({
    milestone,
    status,
    disabled,
    active,
    headingLevel,
}: MilestoneCardProps) => {
    const navigate = useNavigate()

    const headingId = uuid()
    const checkmarkId = uuid()
    const statusBadgeId = uuid()
    const footerId = uuid()
    const {illustration, heading, description, footerText, ctaText, onCtaClick} = getContent({
        milestone,
        status,
        disabled,
        navigate,
    })

    return (
        <Container name={CONTAINER_QUERY_NAME}>
            <OuterWrapper
                aria-labelledby={headingId}
                aria-describedby={[footerId, statusBadgeId, checkmarkId].join(' ')}
                $active={active}
            >
                <Main>
                    <Content>
                        {status !== 'COMPLETE' && <Illustration src={illustration} alt="" />}

                        <Inline space={16} alignY="center">
                            {status === 'COMPLETE' && (
                                <CheckCircleFilled
                                    color={tokens.colorBorderPositive}
                                    id={checkmarkId}
                                    aria-label="Completed"
                                />
                            )}

                            <Stack space={4}>
                                <Inline space={8}>
                                    <Text as={headingLevel} variant="large-accent" weight="medium">
                                        {status === 'IN_PROGRESS' ? (
                                            <>
                                                <Box id={headingId} as="span" mr={8}>
                                                    {heading}
                                                </Box>

                                                <Badge id={statusBadgeId} variant="info">
                                                    In progress
                                                </Badge>
                                            </>
                                        ) : (
                                            <span id={headingId}>{heading}</span>
                                        )}
                                    </Text>
                                </Inline>

                                {description && status !== 'COMPLETE' && (
                                    <Text variant="medium-default">{description}</Text>
                                )}
                            </Stack>
                        </Inline>
                    </Content>

                    {status === 'INCOMPLETE' && (
                        <Button
                            variant="primary"
                            disabled={disabled}
                            onClick={onCtaClick}
                            IconRight={disabled ? Lock : ArrowRight}
                        >
                            {ctaText}
                        </Button>
                    )}
                </Main>

                {footerText && <Footer id={footerId}>{footerText}</Footer>}
            </OuterWrapper>
        </Container>
    )
}

type FooterProps = {
    id: string
    children: React.ReactNode
}

const Footer = ({id, children}: FooterProps) => {
    const isWide = useContainerQuery({
        name: CONTAINER_QUERY_NAME,
        minWidth: MOBILE_BREAKPOINT,
    })

    return (
        <StyledFooter id={id}>
            <Text
                variant={isWide ? 'medium-default' : 'small-subtle'}
                color="colorContentStaticQuiet"
            >
                {children}
            </Text>
        </StyledFooter>
    )
}

//#endregion Components

//#region Helpers

type Content = {
    illustration: string
    heading: string
    description?: React.ReactNode
    footerText?: React.ReactNode
    ctaText: string
    onCtaClick: () => void
}

type GetContentArgs = {
    milestone: Milestone
    status: Status
    disabled?: boolean
    navigate: NavigateFunction
}

const getContent = ({milestone, status, disabled, navigate}: GetContentArgs): Content => {
    switch (milestone) {
        case 'COMPANY_VERIFICATION':
            return {
                illustration: magnifyingGlassIllustration,
                heading: 'Get verified',
                description:
                    status === 'IN_PROGRESS' ? (
                        <span>
                            Your documents are being reviewed by an analyst at Pleo. The usual wait
                            time is <strong>1-2 business days</strong>.
                        </span>
                    ) : (
                        'Verify your company to try Pleo for free'
                    ),
                ctaText: 'Continue',
                onCtaClick: () => navigate('/company-verification'),
            }
        case 'WALLET':
            return {
                illustration: walletAddIllustration,
                heading: 'Add money to your account',
                description:
                    status === 'IN_PROGRESS' ? (
                        <span>
                            Available within 2-3 business days, after you've made the transfer.
                            Haven't made the transfer?{' '}
                            <Button
                                variant="link"
                                onClick={() => {
                                    // TODO: Maybe open a modal?
                                    // Will be tackled in: https://linear.app/pleo/issue/HEI-3525
                                }}
                            >
                                View transfer credentials
                            </Button>
                            .
                        </span>
                    ) : undefined,
                footerText: disabled ? 'Your account needs to be verified to do this.' : undefined,
                ctaText: 'Continue',
                onCtaClick: () => navigate('/onboarding/pleo-guide/topup-wallet'),
            }

        default:
            return exhaustiveCheck(milestone)
    }
}

//#endregion Helpers

//#region Styles

const activeStyles = css`
    border-color: ${tokens.colorBorderStaticLoud};
    box-shadow: ${tokens.shadowElevateQuiet};
`

type OuterWrapperProps = {
    $active?: boolean
}

const OuterWrapper = styled.section<OuterWrapperProps>`
    border-radius: ${tokens.arc8};
    border: ${tokens.borderPrimary};
    box-sizing: border-box;

    ${({$active}) => $active && activeStyles}

    * {
        box-sizing: border-box;
    }
`

const Main = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    column-gap: ${tokens.spacing24};
    row-gap: ${tokens.spacing8};
    padding: ${tokens.spacing24};
    align-items: center;
    justify-items: start;

    ${containerQuery(
        {name: CONTAINER_QUERY_NAME, minWidth: MOBILE_BREAKPOINT},
        css`
            grid-template-columns: 1fr auto;
        `,
    )}
`

const Content = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    align-items: center;
    column-gap: ${tokens.spacing16};
    row-gap: ${tokens.spacing8};

    ${containerQuery(
        {name: CONTAINER_QUERY_NAME, minWidth: MOBILE_BREAKPOINT},
        css`
            grid-template-columns: auto 1fr;
        `,
    )}
`

const Illustration = styled.img`
    width: 48px;
    height: 48px;
    object-fit: contain;
`

const StyledFooter = styled.footer`
    background: ${tokens.colorBackgroundStaticLoud};
    padding: ${tokens.spacing16} ${tokens.spacing24};
    border-top: ${tokens.borderPrimary};
`

//#endregion Styles
